import * as React from 'react';
const { useMemo } = React;
import { Marked } from 'marked';
import DOMPurify from 'dompurify';
import type { ChatMessage, ActionProposal, ContextJob } from '../types/assistant';
import { AssistantActionCard } from './AssistantActionCard';

// Isolated marked instance to avoid mutating the global
const chatMarked = new Marked({ breaks: true, gfm: true });

/** Parse markdown and sanitize with DOMPurify to prevent XSS */
export function renderMarkdown(text: string): string {
  const raw = chatMarked.parse(text) as string;
  return DOMPurify.sanitize(raw);
}

interface ExtractedLink {
  href: string;
  text: string;
}

/**
 * Pull <a> tags out of the assistant's rendered HTML so they can be surfaced
 * as buttons below the message bubble instead of appearing as inline prose links.
 *
 * Strategy: parse the sanitized HTML, capture each anchor's href and text,
 * then replace the anchor in-place with its bare text content (preserving
 * surrounding sentence flow). Returns the modified HTML plus the link list.
 *
 * No-op on SSR or any environment without window.DOMParser.
 */
function extractLinks(html: string): { cleanedHtml: string; links: ExtractedLink[] } {
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return { cleanedHtml: html, links: [] };
  }

  const doc = new window.DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const container = doc.body.firstElementChild;
  if (!container) return { cleanedHtml: html, links: [] };

  const links: ExtractedLink[] = [];
  const seen = new Set<string>();
  const anchors = Array.from(container.querySelectorAll('a'));

  anchors.forEach((a) => {
    const href = a.getAttribute('href') || '';
    const text = (a.textContent || '').trim();
    if (href && !seen.has(href)) {
      seen.add(href);
      links.push({ href, text: text || href });
    }
    // Replace the anchor with its plain text so the surrounding sentence still reads
    const textNode = doc.createTextNode(text);
    a.replaceWith(textNode);
  });

  return { cleanedHtml: container.innerHTML, links };
}

interface AssistantMessageProps {
  message: ChatMessage;
}

export const AssistantMessage: React.FC<AssistantMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';

  const { cleanedHtml, extractedLinks } = useMemo(() => {
    if (isUser) return { cleanedHtml: null as string | null, extractedLinks: [] as ExtractedLink[] };
    const raw = renderMarkdown(message.content);
    const { cleanedHtml: ch, links } = extractLinks(raw);
    return { cleanedHtml: ch, extractedLinks: links };
  }, [message.content, isUser]);

  // Backwards-compat alias for the action_proposal branch below
  const sanitizedHtml = cleanedHtml;
  const contextJob = (message.structured_data as { context_job?: ContextJob } | undefined)?.context_job;

  const bubbleStyle: React.CSSProperties = {
    maxWidth: '85%',
    padding: '8px 12px',
    borderRadius: 12,
    marginBottom: 8,
    fontSize: 13,
    lineHeight: 1.5,
    wordBreak: 'break-word',
    ...(isUser
      ? {
          background: '#2C5282',
          color: '#fff',
          marginLeft: 'auto',
          borderBottomRightRadius: 4,
          whiteSpace: 'pre-wrap' as const,
        }
      : {
          background: '#f7fafc',
          color: '#1a202c',
          borderBottomLeftRadius: 4,
        }),
  };

  if (message.content_type === 'action_proposal' && message.structured_data) {
    const actionHtml = message.content ? renderMarkdown(message.content) : '';
    return (
      <div style={{ marginBottom: 8 }}>
        {message.content && (
          <div
            style={{ ...bubbleStyle, background: '#f7fafc', color: '#1a202c' }}
            className="didi-markdown"
            dangerouslySetInnerHTML={{ __html: actionHtml }}
          />
        )}
        <AssistantActionCard data={message.structured_data as ActionProposal} />
        <ContextJobLink job={contextJob} />
      </div>
    );
  }

  // User messages: plain text only
  if (isUser) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div style={bubbleStyle}>{message.content}</div>
      </div>
    );
  }

  // Assistant messages: render markdown (sanitized via DOMPurify), then any
  // links extracted from the prose render as buttons below the bubble.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <div
        className="didi-markdown"
        style={bubbleStyle}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml! }}
      />
      <ExtractedLinkButtons links={extractedLinks} />
      <ContextJobLink job={contextJob} />
    </div>
  );
};

interface ContextJobLinkProps {
  job?: ContextJob;
}

const contextJobLinkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  marginTop: 2,
  marginBottom: 8,
  padding: '5px 12px',
  fontSize: 12,
  fontFamily: 'inherit',
  color: '#2C5282',
  background: '#fff',
  border: '1px solid #bee3f8',
  borderRadius: 16,
  cursor: 'pointer',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  maxWidth: '85%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const ContextJobLink: React.FC<ContextJobLinkProps> = ({ job }) => {
  if (!job) return null;
  return (
    <a href={job.url} style={contextJobLinkStyle}>
      <i className="fas fa-briefcase" style={{ fontSize: 11 }} />
      View job: {job.title}
    </a>
  );
};

interface ExtractedLinkButtonsProps {
  links: ExtractedLink[];
}

const extractedLinkContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginTop: -2,
  marginBottom: 8,
};

const extractedLinkButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 12px',
  fontSize: 12,
  fontFamily: 'inherit',
  color: '#fff',
  background: '#2C5282',
  border: 'none',
  borderRadius: 16,
  cursor: 'pointer',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

function buttonLabel(link: ExtractedLink): string {
  const t = link.text.trim();
  if (!t) return link.href;
  return t.replace(/[.,;:!?]+$/, '');
}

function isExternal(href: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const u = new URL(href, window.location.origin);
    return u.origin !== window.location.origin;
  } catch {
    return false;
  }
}

const ExtractedLinkButtons: React.FC<ExtractedLinkButtonsProps> = ({ links }) => {
  if (!links.length) return null;
  return (
    <div style={extractedLinkContainerStyle}>
      {links.map((link, i) => {
        const external = isExternal(link.href);
        return (
          <a
            key={i}
            href={link.href}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
            style={extractedLinkButtonStyle}
          >
            {buttonLabel(link)}
            <i className="fas fa-arrow-right" style={{ fontSize: 11 }} />
          </a>
        );
      })}
    </div>
  );
};
